import React, { ReactElement } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import themestyle from "../theme";

interface ConfirmDialogProps {
  open: boolean;
  icon?: ReactElement<any>;
  message: string;
  onConfirm: () => void;
  onClose: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmButtonColor?: 'primary' | 'danger' | string;
  iconBgColor?: "primary" | "danger" | "success" | "warning" | string; 
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  icon,
  message,
  onConfirm,
  onClose,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmButtonColor = 'primary',
  iconBgColor,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // true if screen < 600px

  const resolveConfirmBg = () => {
    if (confirmButtonColor === 'primary') return themestyle.colors.primary || '#1565C0';
    if (confirmButtonColor === 'danger') return '#EB2857';
    return confirmButtonColor;
  };
  const confirmBg = resolveConfirmBg();

  const iconBg = confirmButtonColor === 'danger' ? '#FFECEC' : '#E7F0FF';

 const getIconBgColor = (type?: string) => {
  switch (type) {
    case "danger":
      return "#FDE2E1"; 
    case "success":
      return "#E6F4EA"; 
    case "warning":
      return "#FFF4E5";
    case "primary":
      return "#E7F0FF"; 
    default:
      return type || "#F3F4F6";
  }
};



  const actionButtonBaseSx = {
    textTransform: 'none',
    fontSize: '0.85rem',
    borderRadius: '10px',
    width: '100%',
    maxWidth: isMobile ? '90%' : 320,
    mx: 'auto',
    height: '35px',
  } as const;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 3,
          textAlign: "center",
          padding: isMobile ? 2 : 1,
          maxWidth: isMobile ? '90%' : 340,
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle sx={{ pb: 1 }}>
        {icon && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              bgcolor: iconBgColor ? getIconBgColor(iconBgColor) : "transparent",
              borderRadius: '50%',
              width: isMobile ? 50 : 60,
              height: isMobile ? 50 : 60,
              alignItems: 'center',
              mb: 1,
              margin: '0 auto',
            }}
          >
            {React.cloneElement(icon as ReactElement<any>, {
              sx: {
                ...(((icon as ReactElement<any>).props?.sx as Record<string, unknown>) || {}),
                color: confirmBg,
              },
            })}
          </Box>
        )}
      </DialogTitle>

      <DialogContent>
        <DialogContentText
          sx={{
            fontSize: isMobile ? "0.85rem" : "0.95rem",
            color: "text.secondary",
            paddingX: 3,
            marginBottom: 1,
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions
        disableSpacing
        sx={{
          flexDirection: "row",
          gap: 2,
          paddingBottom: 2,
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            ...actionButtonBaseSx,
            color: themestyle.colors.primary,
            backgroundColor: '#EFEFEF',
            borderColor: themestyle.colors.primary,
            "&:hover": {
              borderColor: themestyle.colors.primary,
              color: themestyle.colors.primary,
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          sx={{ ...actionButtonBaseSx, bgcolor: confirmBg, color: '#FFFFFF' }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
